/**
 * The viewport VOCABULARY: presets, zoom bounds, persistence, and the module
 * state that answers "which breakpoint am I editing".
 *
 * ── What left, and why (CMD-5) ────────────────────────────────────────────
 * This file used to hold a `ViewportControls` class that was two products in a
 * trench coat — a preset switcher and a zoom control — joined by one shared
 * `_fitScale` number. It was allowlisted in FOUR sweep rules at once
 * (`no-hand-rolled-button`, `no-hand-rolled-btn-class`, `no-inline-svg-literal`,
 * `no-sized-svg-attr`), which is a fair summary of what it was.
 *
 * The class is now `ui/command-bar/viewport-switcher.js` +
 * `ui/command-bar/zoom-control.js`, both built on the kit, and all four
 * allowlist entries shrank in the same commit.
 *
 * What stays here is what a dozen other modules import: `VIEWPORTS`,
 * `getActiveViewportName()`, the responsive-edit scope, and the zoom clamp and
 * persistence helpers. This module now holds NO DOM — which is why it can be
 * imported by anything, including the two halves that replaced its class.
 */

import { eventBus } from '../core/events.js';

/**
 * The three presets. NO `icon` field any more — the glyphs live in
 * `ui/icons.js` as `desktop` / `tablet` / `mobile`.
 *
 * They were attribute-sized inline SVG literals inside a config object, which
 * is exactly why they survived so long: the sweep reads markup, and nobody
 * scans a data table looking for a sized glyph literal.
 */
export const VIEWPORTS = [
  { name: 'desktop', width: '100%', height: '100%', label: 'Desktop' },
  { name: 'tablet', width: '768px', height: '100%', label: 'Tablet' },
  { name: 'mobile', width: '375px', height: '100%', label: 'Mobile' }
];

const RESPONSIVE_SCOPE_VALUES = new Set(['viewport', 'all']);
let activeViewportName = 'desktop';
let responsiveEditScope = 'viewport';

/** Current named preview viewport, shared by responsive override writers. */
export function getActiveViewportName() {
  return activeViewportName;
}

/**
 * Display label for a preset name (`desktop` → `Desktop`). The responsive-scope
 * toggle names the viewport it would write to rather than saying "This size",
 * so the control states the target instead of describing it. One lookup, so the
 * three surfaces carrying that toggle cannot drift into three vocabularies.
 */
export function viewportLabel(name) {
  return VIEWPORTS.find((preset) => preset.name === name)?.label || 'This size';
}

/** Whether responsive controls target only the named viewport or every size. */
export function getResponsiveEditScope() {
  return responsiveEditScope;
}

export function setResponsiveEditScope(scope) {
  const next = RESPONSIVE_SCOPE_VALUES.has(scope) ? scope : 'viewport';
  if (next === responsiveEditScope) return false;
  responsiveEditScope = next;
  eventBus.emit('responsive-edit-scope:changed', {
    scope: next,
    viewport: activeViewportName
  });
  return true;
}

/** Null means write the all-size/base override; otherwise write a named bucket. */
export function getResponsiveOverrideViewport() {
  return responsiveEditScope === 'all' ? null : activeViewportName;
}

export const ZOOM_STEPS = [0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5];
export const ZOOM_DEFAULT = 1;
export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 1.5;
const ZOOM_STORAGE_KEY = 'pb-preview-zoom';

/** Clamp a zoom level into the supported range, rounded to whole percent. */
export function clampZoom(level) {
  const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number(level) || ZOOM_DEFAULT));
  return Math.round(next * 100) / 100;
}

/**
 * Phase 11: read the persisted preview zoom (view pref — not document state, so
 * localStorage, never exportable state). Returns null when absent/invalid so the
 * caller falls back to ZOOM_DEFAULT.
 */
export function readPersistedZoom(storage) {
  try {
    const raw = storage?.getItem(ZOOM_STORAGE_KEY);
    if (raw == null) return null;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < ZOOM_MIN || value > ZOOM_MAX) return null;
    return clampZoom(value);
  } catch {
    return null;
  }
}

export function writePersistedZoom(storage, zoom) {
  try {
    storage?.setItem(ZOOM_STORAGE_KEY, String(zoom));
  } catch {
    /* private-mode / quota — zoom persistence is best-effort */
  }
}

/**
 * The single writer of the module's active-preset state.
 *
 * `ViewportSwitcher` calls it; nothing else may. `getActiveViewportName()` is
 * read by a dozen modules that resolve responsive overrides, and two writers
 * would mean two of them disagreeing about which breakpoint an override
 * belongs to.
 */
export function _setActiveViewportName(name) {
  activeViewportName = name;
}
